import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  digitsOf,
  type CloseMethod,
  type ExpandPayload,
  type StartChatTarget,
} from '@chat-emp/shared';

import {
  useChatsUnreadCount,
  useFacetsBy,
  useInboxList,
  useInboxTotal,
  useInfoUnread,
  useMessageHits,
} from '../data/inbox.js';
import { useStackedApplications } from '../data/stacks.js';
import { useConversationRow, useInbox, useLiveReady } from '../data/source.js';
import { useConversationOpened } from '../hooks/useConversationOpened.js';
import type { OpenRequest } from '../hooks/useStartChatRequest.js';
import type { ChannelId, InboxItem, ParticipantId } from '../fixtures/domain.js';
import { matchesSearch } from '../fixtures/rules.js';
import { announceStarted } from './announceStarted.js';
import { ComposeView } from './ComposeView.js';
import type { ResultList } from '../inbox/InboxPanel.js';
import { buildJobTree, selectableIds, type JobTreeNode } from '../inbox/job-tree.js';
import { filterListItems, listJobTree } from '../inbox/list-filter.js';
import { emptyQuery, withFilter } from '../inbox/query.js';
import { groupByPerson, stackRows } from '../inbox/stack.js';
import { cidParts, emitToHost } from '../lib/bridge.js';
import { track } from '../lib/analytics.js';
import { AuthGate } from '../session/AuthGate.js';
import { useStreamClient } from '../session/StreamProvider.js';
import { useDismiss } from '../util/useDismiss.js';
import { WidgetHeader } from './WidgetHeader.js';
import { JobFilterButton, JobFilterSheet } from './WidgetJobFilter.js';
import { WidgetList } from './WidgetList.js';
import { WidgetSearch } from './WidgetSearch.js';
import { countChannels, joinHits, splitProfileOnly } from './search.js';
import { WidgetThread } from './WidgetThread.js';
import styles from './WidgetPanel.module.css';

/**
 * Every conversation the employer has with a person: job and direct, open or
 * ending, ungrouped, newest first.
 *
 * The phases are the union of `InboxPanel`'s base query and `EndedPanel`'s, so
 * the widget holds exactly what /chat's two rails hold between them — parity by
 * construction rather than by two lists happening to agree. The widget has no
 * room for the rail split and no controls to narrow with, so it reads them as
 * one list and lets the row say which is which (see `ChannelPreview`).
 *
 * `kind` is unchanged and load-bearing: Info/broadcast has its own rail and no
 * reply loop, and career_fair rides the same exclusion.
 *
 * Module-level because `useInboxList` keys its fetch on the serialized query;
 * rebuilding it per render would be a stable *value* either way, but keeping
 * one instance makes that obvious rather than incidental.
 */
const WIDGET_CHATS = withFilter(
  withFilter(emptyQuery(), { kind: 'status', phases: ['open', 'closing', 'pending_delete'] }),
  { kind: 'kind', kinds: ['job', 'direct'] },
);

const NO_MATCHES: ReadonlySet<string> = new Set();

function searchDemo(items: InboxItem[], term: string): InboxItem[] {
  return term
    ? items.filter((item) => matchesSearch(item.channel, item.counterpart.name, term))
    : items;
}

/** Stable identity: `WidgetList` observes on `loadMore`, so a fresh closure per
 *  render would tear the observer down and rebuild it on every render. */
const NOOP = () => {};

/**
 * The ids a selection actually narrows by — none where it names every posting.
 * A `job` clause listing every job still drops the direct chats, which have no
 * job at all, so "everything ticked" has to mean no clause rather than all of them.
 */
function narrowingJobs(tree: JobTreeNode[], picked: string[]): string[] {
  const held = new Set(picked);
  const selectable = selectableIds(tree);
  const all = selectable.length > 0 && selectable.every((id) => held.has(id));
  return all ? [] : picked;
}

/** Stamped on every funnel event this surface fires; `/chat` stamps its own. */
const FUNNEL = { surface: 'widget' } as const;

interface WidgetPanelProps {
  /** Told HOW it was closed, so the host's open/close funnel can distinguish
   *  an escape from the header × (stream-chat parity — see `CloseMethod`). */
  onClose: (method: CloseMethod) => void;
  onExpand: (req: ExpandPayload) => void;
  /** The live unread-chats count, for the launcher that owns the badge and the
   *  host relay — its own poll pauses while we are open. */
  onUnread?: (n: number) => void;
  /** Gate the body on session state (real /widget) vs show fixtures (preview). */
  gate?: boolean;
  /**
   * A host request to open on something. A `channelId` (View / Reply) selects
   * that thread directly; a `target` (Start) opens the composer, because no
   * channel exists yet.
   */
  request?: OpenRequest | null;
  /** The compose ended — sent or cancelled — so the host can drop the request. */
  onComposeDone?: () => void;
  /** The host has not grown the frame yet: stay invisible, animation held. */
  framePending?: boolean;
  /** Single-chat pane: only the chat the host opened, no way back to the list. */
  pane?: boolean;
}

/**
 * The open widget: a list of chats, and one thread at a time.
 *
 * The list is the read model's, same as /chat's — not a client-side cut over
 * every watched channel. That is what keeps the two from disagreeing about
 * which chats exist, and it is why the widget no longer needs `preload`: the
 * page it shows is hydrated by the query, and the thread it opens hydrates
 * itself.
 *
 * The query lives here rather than in `ChatWidget` on purpose — that is what
 * makes a shut widget free. See the mount gate in `ChatWidget` for why.
 */
export function WidgetPanel({
  onClose,
  onExpand,
  onUnread,
  gate,
  request,
  onComposeDone,
  framePending,
  pane,
}: WidgetPanelProps) {
  const { client } = useStreamClient();
  // The pane shows only the chat the host named, so it queries no list.
  const listClient = pane ? null : client;

  /**
   * The fixtures demo at /widget-preview has no Stream client and no read model,
   * so everything the server would answer is answered here from what it holds.
   * The seam splits fixtures the way /chat's rails do; the widget spans both.
   */
  const demo = useInbox();
  const demoItems = useMemo(
    () =>
      [...demo.lists.inbox, ...demo.lists.ended].sort(
        (a, b) => Date.parse(b.channel.lastActivityAt) - Date.parse(a.channel.lastActivityAt),
      ),
    [demo.lists.inbox, demo.lists.ended],
  );

  // Faceted over the base, not the cut: a job offered only where it is already
  // picked could never be widened. `InboxPanel`'s `jobFacetQuery` leaves one out
  // for the same reason. The demo reads its postings off the chats it holds —
  // flat, since a fixture carries no parent job.
  const jobFacets = useFacetsBy('job', WIDGET_CHATS, Boolean(listClient));
  const jobTree = useMemo(
    () => (client ? buildJobTree(jobFacets.facets) : listJobTree(demoItems)),
    [client, jobFacets.facets, demoItems],
  );

  /**
   * The scope in force, and the one being built in the sheet. Held here so both
   * survive a trip into a conversation, and die with the panel: a scope the
   * employer cannot see on opening hides chats they came to find.
   *
   * Two, because the list sits behind the sheet. Re-querying on every tick would
   * spend a page fetch and a Stream `queryChannels` on a cut nobody is looking at.
   */
  const [jobIds, setJobIds] = useState<string[]>([]);
  const [draftIds, setDraftIds] = useState<string[]>([]);
  const [filterOpen, setFilterOpen] = useState(false);

  const scope = useMemo(() => narrowingJobs(jobTree, jobIds), [jobTree, jobIds]);
  const draftScope = useMemo(() => narrowingJobs(jobTree, draftIds), [jobTree, draftIds]);
  const narrowed = scope.length > 0;

  // Debounced like `/chat`'s box, and for the same reason: each settle is a read-model query.
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search), 250);
    return () => clearTimeout(timer);
  }, [search]);
  const term = debounced.trim();

  const cutBy = (ids: string[]) => {
    let q = WIDGET_CHATS;
    if (term) q = withFilter(q, { kind: 'search', text: term, field: 'any' });
    return ids.length === 0 ? q : withFilter(q, { kind: 'job', jobIds: ids });
  };

  // Server-side, so the cut reaches every conversation rather than the page in hand.
  const query = useMemo(() => cutBy(scope), [scope, term]);

  const live = useInboxList(query, listClient);
  const { items, loading, error, hasMore, loadingMore, loadMore, reload } = client
    ? live
    : {
        // Cut here rather than on a query, because there is no server to ask.
        items: searchDemo(filterListItems(demoItems, scope, []), term),
        loading: demo.loading,
        error: false,
        // Fixtures are one page by construction — there is nothing to fetch.
        hasMore: false,
        loadingMore: false,
        loadMore: NOOP,
        reload: demo.reload,
      };
  // Whether the gate is currently standing in for the body — the same signal
  // `AuthGate` decides on, so this cannot drift from what it actually renders.
  const gated = Boolean(gate) && !useLiveReady();

  /**
   * The several live applications one person holds, so three chats with the same
   * face draw as one row here too (see `stackRows`).
   *
   * The live list is paged, so membership is asked of the read model — the same
   * hook /chat's inbox uses, over this panel's own query, so the answer is
   * narrowed to exactly what the widget shows. The fixtures demo has no client
   * and no paging: its list IS the whole set, so it folds from what it holds,
   * the way a saved list's detail does.
   */
  const liveApplications = useStackedApplications(query, items, listClient);
  const applications = client ? liveApplications : groupByPerson(items);
  const allRows = useMemo(() => stackRows(items, applications), [items, applications]);

  // The fixtures demo has no BFF to ask, so it finds no messages.
  const { hits, term: hitTerm } = useMessageHits(client ? term : '');
  const hitRows = useMemo(() => joinHits(hits, items, hitTerm), [hits, items, hitTerm]);
  const profileMatched = client ? live.profileMatched : NO_MATCHES;
  const [rows, candidateRows] = useMemo(
    () => splitProfileOnly(allRows, profileMatched, term),
    [allRows, profileMatched, term],
  );

  // One search is one visit to the search bar, reported on leaving it — the same
  // rule and payload as `/chat`'s `search_performed` (AI-3011), stamped widget.
  const searchSnapshot = useRef({ query: '', chats: 0, messages: 0, candidates: 0 });
  searchSnapshot.current = {
    query: search.trim(),
    chats: countChannels(rows),
    messages: hitRows.length,
    candidates: countChannels(candidateRows),
  };
  const searchArmed = useRef(true);
  const endSearch = useCallback(() => {
    const { query, chats, messages, candidates } = searchSnapshot.current;
    if (query === '' || !searchArmed.current) return;
    searchArmed.current = false;
    track('search_performed', {
      ...FUNNEL,
      query,
      results_chats: chats,
      results_messages: messages,
      results_jobseeker_details: candidates,
    });
  }, []);
  // Closing the widget unmounts the panel, which is leaving the search bar too.
  useEffect(() => () => endSearch(), [endSearch]);

  // What the sheet's Show button promises: a `limit: 1` count for the picks in
  // hand, which answers only for the query it was asked and costs no hydration.
  const draftQuery = useMemo(() => cutBy(draftScope), [draftScope, term]);
  const liveCount = useInboxTotal(draftQuery, filterOpen && Boolean(client));
  const shownCount = client
    ? liveCount
    : searchDemo(filterListItems(demoItems, draftScope, []), term).length;

  /** Which folded rows are open, by person — see `StackedPreview`. */
  const [expandedPeople, setExpandedPeople] = useState<ParticipantId[]>([]);
  const expandedSet = useMemo(() => new Set(expandedPeople), [expandedPeople]);
  const toggleExpand = (id: ParticipantId) =>
    setExpandedPeople((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  const [selectedId, setSelectedId] = useState<ChannelId | null>(null);
  const [composeTarget, setComposeTarget] = useState<StartChatTarget | null>(null);
  const [composeDraft, setComposeDraft] = useState<string | undefined>(undefined);

  // Keyed on `requestId` and not the payload: the host may ask twice with an
  // identical body — a second click on the same Chat button — and only the id
  // moved. Watching the object would ignore the second ask.
  useEffect(() => {
    if (!request) return;
    if (request.channelId) {
      // View / Reply — straight to the thread. No compose in between, and no
      // resolution step: the host already knows this conversation exists.
      setComposeTarget(null);
      setSelectedId(request.channelId as ChannelId);
      return;
    }
    if (request.target) {
      setSelectedId(null);
      setComposeTarget(request.target);
      setComposeDraft(request.draft);
    }
    // `request` itself is deliberately not a dependency — see above.
  }, [request?.requestId]);

  // Relay the open thread to the host, same as /chat does for its selection.
  useConversationOpened(selectedId ?? undefined);
  // Escape only — note the omitted ref, which is what turns off click-outside.
  // The widget floats over the employer's actual work, so a click on the page
  // behind it is them using the host site, not dismissing us. Throwing away a
  // half-typed reply for that is a trap /chat never has to spring.
  //
  // Off while a dialog is over the thread: Escape belongs to the innermost thing
  // on screen, and closing the widget from under an open template panel would
  // take the panel, the thread and the half-written reply with it.
  const [modalOpen, setModalOpen] = useState(false);
  // The sheet is the innermost thing open, so it takes the press first — and
  // leaves by the same door the button uses, or Escape would discard the picks.
  useDismiss(!modalOpen, filterOpen ? () => closeFilter() : () => onClose('esc'));

  // Compared on the bare id, never the raw string: `channel.id` is Stream's cid
  // (`messaging:chat-comp-…`) while a host — and our own POST /api/chat/start —
  // supplies the canonical `chat-comp-…`. A strict compare silently falls back
  // to the list, which reads as View/Reply not working at all.
  const wantedId = selectedId ? cidParts(selectedId).channelId : null;
  const listed =
    (wantedId ? items.find((item) => cidParts(item.channel.id).channelId === wantedId) : null) ??
    null;
  // The list is not the index of every chat — it is the pages loaded so far, 25
  // at a time, newest first. So scanning it answers "is this chat near the top
  // of the inbox", which is not the question a host asked. View/Reply on a
  // two-day-old applicant names a chat that is not in `items` and, unless the
  // employer scrolls far enough, never will be; the panel then rendered the
  // list, which is the same thing it renders when nothing was requested at all.
  //
  // Hydrating the one channel is how /chat has always resolved `?channel=`
  // (`ChatView`'s `hydratedRow`), and it lands independently of the list query —
  // so the thread opens on what the host named, not on where it happens to sort.
  const hydrated = useConversationRow(selectedId);
  // List first, so a chat that was already loaded resolves exactly as before and
  // the fallback is only ever reached by one the scan could not answer for.
  const selected = listed ?? hydrated;

  // Whether the header is over the bare list, which is the only place the Info
  // megaphone is drawn.
  //
  // Not because leaving would cost anything — a compose draft survives the trip
  // (see `composeDraft`), and `onExpand` beside it makes the same trip anyway.
  // Because of what the header is naming. Over the list it names the inbox, and
  // Info is the inbox's sibling; over a compose or a held-back thread it names
  // one person, and a section is not something to offer from there. Narrower
  // than the header's own render condition for that reason: the gated case
  // exists to give a thread a back and a ×, not somewhere else to go.
  //
  // The ask it sends carries no channel: Info is a section, not a chat, so it
  // names where to land and nothing to open there.
  const overList = !composeTarget && !selected && !pane;

  // A host's View request can open a thread from under an open sheet, which
  // would then hold an Escape nobody can see it holding.
  useEffect(() => {
    if (!overList) setFilterOpen(false);
  }, [overList]);
  // The same answer the rail draws at /chat, from the same hook, so the two
  // surfaces cannot disagree about whether an announcement is waiting. (It is
  // also the one unread the launcher's badge deliberately ignores: a marketing
  // thread is never replied to, so counting it there would be a mark nothing an
  // employer does can clear.) Watches no channel and opens no connection, and
  // runs whether or not the megaphone is currently drawn — see the hook for why
  // pausing it cost more than it saved.
  const infoUnread = useInfoUnread(listClient);
  const unread = useChatsUnreadCount(listClient);
  useEffect(() => {
    if (unread !== null) onUnread?.(unread);
  }, [unread, onUnread]);

  // Note the absence of ChatPage's auto-select-first-channel effect. On a page
  // the employer deliberately navigated to, opening the top chat is a fair
  // guess. On a badge they clicked to find out *who* is waiting, it isn't: it
  // would mark the newest chat read, and a read is not a private act — Stream
  // sends the applicant a receipt, so their message shows as read by a person
  // who never saw it. Answering slowly is a bad look; appearing to have read
  // and ignored them is a worse one. Nothing is read until it's chosen.
  //
  // It now also touches the launcher's mark, which is unread rather than the
  // pending count it used to be: a read the employer never made would clear the
  // dot as well as sending the receipt. That is a second reason not to do this,
  // not a new one — the receipt was always the harm worth naming.
  //
  // Selecting is all this does to the read: `useConversation` reports it once the
  // thread opens, so /chat and the widget cannot drift on when that happens.
  //
  // `channel_selected` carries `/chat`'s payload, stamped widget. Host View/Reply
  // and a sent compose select without it: neither is a list click.
  const open = (channelId: ChannelId, resultList: ResultList) => {
    endSearch();
    const { channelType, channelId: id } = cidParts(channelId);
    track('channel_selected', {
      ...FUNNEL,
      channel_id: id,
      channel_type: channelType,
      filter_active: narrowed,
      search_active: search.trim() !== '',
      result_list: resultList,
      facets_applied: narrowed ? ['job'] : [],
      values_count: { job: scope.length },
      results_count: client ? live.total : items.length,
    });
    setSelectedId(channelId);
  };

  const viewProfile = (item: InboxItem) => {
    emitToHost('view-profile', {
      channelId: cidParts(item.channel.id).channelId,
      jobseekerId: digitsOf(item.counterpart.id),
    });
    // A phone has no room beside the pane, so it steps aside for the host's profile.
    if (document.documentElement.dataset.viewport === 'mobile') onClose('panel-header');
  };

  // Only the panel's own list needs re-reading; the badge behind it is hidden
  // while we're open, and `ChatWidget` re-counts on close.
  const sent = () => void reload();

  // A union on the way in and a filter on the way out, never a concatenation:
  // under a search the visible ids overlap what is already picked.
  const takeManyJobs = (ids: string[], on: boolean) =>
    setDraftIds((current) =>
      on ? [...new Set([...current, ...ids])] : current.filter((held) => !ids.includes(held)),
    );

  const openFilter = () => {
    setDraftIds(jobIds);
    setFilterOpen(true);
    track('filter_opened', { ...FUNNEL, already_applied: jobIds.length > 0 ? ['job'] : [] });
  };

  /** The sheet's only exit, so every way out commits the same way. */
  const closeFilter = () => {
    setFilterOpen(false);
    setJobIds(draftIds);
    const same = draftIds.length === jobIds.length && draftIds.every((id) => jobIds.includes(id));
    if (same) return;
    track('filter_applied', {
      ...FUNNEL,
      facets_applied: draftIds.length > 0 ? ['job'] : [],
      values_count: draftIds.length,
      ...(shownCount === null ? {} : { results_count: shownCount }),
    });
  };

  // The sheet's footer and the empty list's way out: one rule, reported once.
  // Shut, there is no close left to commit on, so it lands straight away.
  const clearScope = () => {
    track('filter_cleared', { ...FUNNEL, facet: 'all' });
    setDraftIds([]);
    if (!filterOpen) setJobIds([]);
  };

  const body = composeTarget ? (
    <ComposeView
      target={composeTarget}
      // Only known once Stream has connected. Empty means the draft is not
      // persisted yet rather than persisted under a key that would change.
      employerId={client?.userID ?? ''}
      initialDraft={composeDraft}
      onSent={(channelId) => {
        announceStarted(request?.requestId, composeTarget, channelId);
        setComposeTarget(null);
        setSelectedId(channelId as ChannelId);
        // The list behind the composer has no idea this conversation now exists.
        void reload();
        onComposeDone?.();
      }}
      onCancel={() => {
        setComposeTarget(null);
        onComposeDone?.();
        if (pane) onClose('panel-header');
      }}
    />
  ) : selected ? (
    <WidgetThread
      key={selected.channel.id}
      item={selected}
      onSent={sent}
      onModal={setModalOpen}
      {...(pane
        ? { pane, onViewProfile: () => viewProfile(selected) }
        : { onBack: () => setSelectedId(null) })}
      onClose={() => onClose('panel-header')}
      // The thread knows which acts leave; which chat leaves with them is this
      // panel's to say, since it is what holds the selection.
      onExpand={(intent) =>
        onExpand({ channelId: selected.channel.id, ...(intent ? { open: intent } : {}) })
      }
    />
  ) : pane ? (
    <p className={styles.opening}>Opening chat…</p>
  ) : (
    <div className={styles.home}>
      {/* The widget HOME only: inside a conversation the scope is settled. The
          filter only where there are postings to cut by. */}
      <div className={styles.toolbar}>
        <WidgetSearch
          value={search}
          onChange={setSearch}
          // Re-armed on focus: a visit is the unit, so leaving the box twice is two searches.
          onFocus={() => {
            searchArmed.current = true;
          }}
          onBlur={endSearch}
        />
        {jobTree.length > 0 && (
          <JobFilterButton
            narrowed={narrowed}
            open={filterOpen}
            onClick={() => (filterOpen ? closeFilter() : openFilter())}
          />
        )}
      </div>
      <WidgetList
        rows={rows}
        search={
          search.trim() === ''
            ? null
            : {
                term,
                hitTerm,
                hitRows,
                candidateRows,
                // No BFF behind the fixtures demo, so only the debounce holds it back.
                pending: (client ? hitTerm : term) !== search.trim(),
              }
        }
        loading={loading}
        error={error}
        hasMore={hasMore}
        loadingMore={loadingMore}
        loadMore={loadMore}
        onOpen={open}
        expandedPeople={expandedSet}
        onToggleExpand={toggleExpand}
        onClearFilter={narrowed ? clearScope : undefined}
      />
      {filterOpen && (
        <JobFilterSheet
          tree={jobTree}
          selected={draftIds}
          onChange={setDraftIds}
          onSelectMany={takeManyJobs}
          onClear={clearScope}
          onClose={closeFilter}
          shownCount={shownCount}
        />
      )}
    </div>
  );

  return (
    <div
      className={styles.panel}
      role="dialog"
      aria-label="Chats"
      data-frame-pending={framePending || undefined}
    >
      {/* The thread draws its own header, because the actions that belong in it
          are the conversation's — see `WidgetHeader`'s `actions`. This one
          covers every other state: the list, the composer, and a thread the gate
          is holding back. That last case is why `gated` is here rather than a
          plain `!selected` — the thread's header renders inside `AuthGate`, so a
          conversation open when the connection drops would be left with no back
          and no ×, and on a phone the FAB is hidden and that × is the only exit.
          The condition otherwise mirrors `body`'s: a compose outranks a
          selection. */}
      {(composeTarget || !selected || gated) && (
        <WidgetHeader
          title={
            composeTarget?.applicant.name ?? selected?.counterpart.name ?? (pane ? 'Chat' : 'Chats')
          }
          {...((composeTarget || selected) && !pane
            ? {
                onBack: () => {
                  setComposeTarget(null);
                  setSelectedId(null);
                  if (composeTarget) onComposeDone?.();
                },
              }
            : {})}
          onExpand={() => onExpand(selected ? { channelId: selected.channel.id } : {})}
          {...(overList ? { onInfo: () => onExpand({ tab: 'info' }), infoUnread } : {})}
          onClose={() => onClose('panel-header')}
        />
      )}

      {gate ? <AuthGate>{body}</AuthGate> : body}
    </div>
  );
}
